import { useCallback, useEffect, useRef, useState } from 'react';

export function useInfiniteReveal<T>(items: T[], pageSize = 10) {
	const [visibleCount, setVisibleCount] = useState(pageSize);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const visibleCountRef = useRef(visibleCount);
	const totalRef = useRef(items.length);

	useEffect(() => {
		visibleCountRef.current = visibleCount;
	}, [visibleCount]);

	useEffect(() => {
		totalRef.current = items.length;
		setVisibleCount(pageSize);
	}, [items, pageSize]);

	const sentinelRef = useCallback((node: HTMLElement | null) => {
		if (observerRef.current) {
			observerRef.current.disconnect();
			observerRef.current = null;
		}
		if (!node) return;
		observerRef.current = new IntersectionObserver(entries => {
			if (entries.some(e => e.isIntersecting) && visibleCountRef.current < totalRef.current) {
				setVisibleCount(c => Math.min(c + pageSize, totalRef.current));
			}
		}, { rootMargin: '100px', threshold: 0 });
		observerRef.current.observe(node);
	}, [pageSize]);

	useEffect(() => () => observerRef.current?.disconnect(), []);

	const visibleItems = items.slice(0, visibleCount);
	const hasMore = visibleCount < items.length;

	return { visibleItems, sentinelRef, hasMore, visibleCount };
}

export default useInfiniteReveal;
